import toast from "react-hot-toast";
import { useMutation } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

const CONFLICT_STATUS = 409;
const PDF_CONTENT_TYPE = "application/pdf";

const isPdfResponse = (contentType, blob) => {
  if (typeof contentType === "string" && contentType.includes(PDF_CONTENT_TYPE))
    return true;

  return blob instanceof Blob && blob.type.includes(PDF_CONTENT_TYPE);
};

const parseBlobError = async (data) => {
  if (!(data instanceof Blob)) return data;

  try {
    const text = await data.text();

    return JSON.parse(text);
  } catch {
    return null;
  }
};

const surfaceServerError = ({ status, body }) => {
  const serverMessage = body?.error;

  if (status === CONFLICT_STATUS) {
    toast(serverMessage || "No clinical data available to export");

    return;
  }

  toast.error(serverMessage || "Failed to download clinical data");
};

const triggerPdfDownload = (data, fileLabel) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `cdm-clinical-export-${fileLabel}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Single-review export uses the review-scoped endpoint (KIN-1194). The
// multi-review `count` export (1–3 pages in one PDF) has no review-scoped
// equivalent yet, so it stays on the deprecated patient-scoped route.
const downloadCdmClinicalExportPdfFn = async ({
  patientId,
  reviewId,
  tenantVal,
  count = 1,
}) => {
  const axiosInstance = createAxiosInstance();
  const requestUrl = reviewId
    ? `${tenantVal}/cdm/reviews/${reviewId}/clinical-export/pdf`
    : `${tenantVal}/cdm/patients/${patientId}/clinical-export/pdf?count=${count}`;
  const response = await axiosInstance.get(requestUrl, {
    responseType: "blob",
  });

  // The BE returns errors as a CDM { success, error } JSON body, not a PDF blob.
  // Guard against a 200-with-JSON edge by branching on Content-Type before saving.
  if (!isPdfResponse(response.headers?.["content-type"], response.data)) {
    const body = await parseBlobError(response.data);
    surfaceServerError({ status: response.status, body });

    return;
  }

  triggerPdfDownload(response.data, reviewId ? `review-${reviewId}` : patientId);
};

const handleDownloadError = async (error) => {
  const status = error?.response?.status;
  const body = await parseBlobError(error?.response?.data);
  surfaceServerError({ status, body });
};

export const useDownloadCdmClinicalExportPdf = () =>
  useMutation({
    mutationFn: downloadCdmClinicalExportPdfFn,
    onError: handleDownloadError,
  });
